/**
 * Where listening audio comes from: the device microphone, or a simulated microphone that plays
 * real recordings into a MediaStream (for development without a microphone and for end-to-end
 * tests). Both feed the same Listener, so the whole pipeline is exercised either way.
 */

export interface AudioInput {
  stream: MediaStream;
  close(): void;
}

/**
 * Tells the browser the page plays and records at the same time (Audio Session API, Safari on
 * iOS; feature-detected), so iOS does not switch audio categories when the microphone opens.
 */
function declarePlayAndRecord() {
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  try {
    if (session) session.type = 'play-and-record';
  } catch {
    // not supported in this browser
  }
}

/** Opens the microphone; rejects if permission is denied or no microphone exists. */
export async function openMicrophone(): Promise<AudioInput> {
  declarePlayAndRecord();
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      // Echo cancellation off (owner's decision, 2026-10-12, issue #39): on iPhone every sound the
      // page plays crackles while an echo-cancelled microphone is open (a WebKit bug; ChatGPT's
      // site does the same). It did little for us anyway: the app's voice reached the mic at -17
      // to -30 dB with it on, so the mic is ignored while the app talks and an echo-tail check
      // follows (practiceSession.ts). Noise suppression is off: it can smear the cues that tell
      // /ɹ/ from /w/; level control helps with distance.
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: true,
    },
  });
  return {
    stream,
    close: () => {
      for (const track of stream.getTracks()) track.stop();
    },
  };
}

/** A clip the simulated child can say. */
export interface SimulatedClip {
  url: string;
  /** Practice word or syllable the clip stands for. */
  word: string;
  /** The word actually recorded (may differ slightly, e.g. "rock" for "rocks"). */
  spoken: string;
  /** Clinician rating of the /ɹ/: correct or not. */
  correct: boolean;
  /** /ɹ/ probability from the Python reference pipeline on the same recording. */
  pythonProbability: number;
  /** Free text for logs, e.g. the source corpus and speaker. */
  source: string;
}

/**
 * A microphone that "hears" real recordings: `say` plays a clip into the stream, otherwise it
 * carries silence. It has its own AudioContext, like a physical device: feeding a context's own
 * MediaStream destination back into it stalls Chrome's audio thread.
 */
export class SimulatedMicrophone {
  readonly stream: MediaStream;
  private readonly destination: MediaStreamAudioDestinationNode;
  private readonly buffers = new Map<string, Promise<AudioBuffer>>();
  private current: AudioBufferSourceNode | undefined;

  private readonly context: AudioContext;

  constructor(context = new AudioContext()) {
    this.context = context;
    this.destination = context.createMediaStreamDestination();
    this.destination.channelCount = 1;
    this.stream = this.destination.stream;
    // Stream silence continuously, like a physical microphone: Firefox sends no frames from an
    // idle destination, so the listener would only start (and calibrate its noise floor) mid-word.
    const silence = context.createConstantSource();
    silence.offset.value = 0;
    silence.connect(this.destination);
    silence.start();
  }

  private load(url: string): Promise<AudioBuffer> {
    let buffer = this.buffers.get(url);
    if (!buffer) {
      buffer = fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`simulated clip ${url}: HTTP ${String(r.status)}`);
          return r.arrayBuffer();
        })
        .then((bytes) => this.context.decodeAudioData(bytes));
      this.buffers.set(url, buffer);
    }
    return buffer;
  }

  private async start(url: string, gain: number, maxMs?: number): Promise<AudioBufferSourceNode> {
    const audio = await this.load(url);
    if (this.context.state === 'suspended') await this.context.resume();
    const source = this.context.createBufferSource();
    source.buffer = audio;
    const level = this.context.createGain();
    level.gain.value = gain;
    source.connect(level).connect(this.destination);
    source.start();
    if (maxMs !== undefined) source.stop(this.context.currentTime + maxMs / 1000);
    return source;
  }

  /** The child says the clip at `url` (replacing anything the child was saying); resolves after. */
  async say(url: string): Promise<void> {
    this.current?.stop();
    const source = await this.start(url, 1);
    this.current = source;
    await new Promise<void>((resolve) => {
      source.onended = () => {
        resolve();
      };
    });
  }

  /**
   * Other sound reaching the microphone alongside the child, e.g. the app's own voice leaking
   * back from the speakers (`gain` relative to the recording, cut after `maxMs`).
   */
  async leak(url: string, gain: number, maxMs: number): Promise<() => void> {
    const source = await this.start(url, gain, maxMs);
    return () => {
      try {
        source.stop();
      } catch {
        // already ended
      }
    };
  }

  close(): void {
    this.current?.stop();
    for (const track of this.stream.getTracks()) track.stop();
  }
}
